import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../shared/constants/env';

export interface AddressOption {
  value: string;
  label: string;
}

// Countries/states already exist as real data in the `countries`/`states`
// tables (255 countries, 586 states) — fetch from there instead of
// maintaining a second, hand-typed copy that can drift out of sync.
const getApiOrigin = () => API_BASE_URL.replace(/\/graphql\/?$/, '');

// Scoped to the platform's actual trade routes for now — Poland exists in
// the `countries` table but is currently marked inactive there and has no
// states populated, which is why its state field stays optional in the UI.
const SUPPORTED_COUNTRY_CODES = ['US', 'IN', 'PL'];

const FALLBACK_COUNTRIES: AddressOption[] = [
  { value: 'US', label: 'United States' },
  { value: 'IN', label: 'India' },
  { value: 'PL', label: 'Poland' },
];

let countriesCache: AddressOption[] | null = null;
let countriesPromise: Promise<AddressOption[]> | null = null;

async function fetchCountries(): Promise<AddressOption[]> {
  if (countriesCache) return countriesCache;
  if (!countriesPromise) {
    countriesPromise = fetch(`${getApiOrigin()}/api/v1/reference/countries?codes=${SUPPORTED_COUNTRY_CODES.join(',')}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load countries');
        return res.json();
      })
      .then((data: AddressOption[]) => {
        countriesCache = data;
        return data;
      })
      .catch(() => FALLBACK_COUNTRIES);
  }
  return countriesPromise;
}

const statesCache: Record<string, AddressOption[]> = {};
const statesPromises: Record<string, Promise<AddressOption[]>> = {};

async function fetchStates(country: string): Promise<AddressOption[]> {
  if (!country) return [];
  if (statesCache[country]) return statesCache[country];
  if (!statesPromises[country]) {
    statesPromises[country] = fetch(`${getApiOrigin()}/api/v1/reference/states?country=${encodeURIComponent(country)}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load states');
        return res.json();
      })
      .then((data: AddressOption[]) => {
        statesCache[country] = data;
        return data;
      })
      .catch(() => []);
  }
  return statesPromises[country];
}

/** For React components: countries dropdown options, fetched once and cached. */
export function useCountries(): AddressOption[] {
  const [countries, setCountries] = useState<AddressOption[]>(countriesCache || []);
  useEffect(() => {
    let cancelled = false;
    fetchCountries().then((data) => { if (!cancelled) setCountries(data); });
    return () => { cancelled = true; };
  }, []);
  return countries;
}

/** For React components: state dropdown options for the given country code. */
export function useStatesForCountry(country: string): AddressOption[] {
  const [states, setStates] = useState<AddressOption[]>(country ? (statesCache[country] || []) : []);
  useEffect(() => {
    if (!country) {
      setStates([]);
      return;
    }
    let cancelled = false;
    fetchStates(country).then((data) => { if (!cancelled) setStates(data); });
    return () => { cancelled = true; };
  }, [country]);
  return states;
}

/** For non-component code (e.g. mapping extracted invoice data): plain async lookups. */
export async function getStatesForCountry(country: string): Promise<AddressOption[]> {
  return fetchStates(country);
}
